import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import ReportForm from './ReportForm';
import ReportPreview from './ReportPreview';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export default function ReportTool({ onBack }) {
  const [clients, setClients] = useState([]);
  const [stage, setStage] = useState('form'); // 'form' | 'generating' | 'preview'
  const [reportData, setReportData] = useState(null);
  const [summary, setSummary] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    supabase.from('clients')
      .select('*, keyword_campaigns(id)')
      .order('name')
      .then(({ data }) => { if (data) setClients(data); });
  }, []);

  const handleGenerate = async (data) => {
    setReportData(data);
    setStage('generating');
    setError('');

    try {
      const { keywords, traffic, conversions, actions, commentary, baseline, clientName, reportName, clientData } = data;

      const totalConversions =
        (parseInt(conversions.calls) || 0) +
        (parseInt(conversions.emails) || 0) +
        (parseInt(conversions.forms) || 0) +
        (parseInt(conversions.other) || 0);

      const baselineConversions =
        (parseInt(baseline.calls) || 0) +
        (parseInt(baseline.emails) || 0) +
        (parseInt(baseline.forms) || 0);

      const kwImprovement = (parseInt(keywords.top10) || 0) - (parseInt(baseline.top10) || 0);
      const trafficImprovement = (parseInt(traffic.sessions) || 0) - (parseInt(baseline.traffic) || 0);
      const convImprovement = totalConversions - baselineConversions;

      const prompt = `You are a senior SEO account manager writing a professional monthly report executive summary for a client.

CLIENT: ${clientName}
REPORT: ${reportName}

KEYWORD PERFORMANCE:
- Top 3 positions: ${keywords.top3} (Day 1: ${baseline.top3 || 0}) — change: ${(parseInt(keywords.top3) || 0) - (parseInt(baseline.top3) || 0)}
- Top 10 positions: ${keywords.top10} (Day 1: ${baseline.top10 || 0}) — change: ${kwImprovement}
- Top 20 positions: ${keywords.top20} (Day 1: ${baseline.top20 || 0})
- Position 20+: ${keywords.top20plus} (Day 1: ${baseline.top20plus || 0})
${commentary.highlightedRankings ? 'Highlighted rankings: ' + commentary.highlightedRankings : ''}

TRAFFIC:
- Sessions this month: ${traffic.sessions || 0} (Day 1: ${baseline.traffic || 0}) — change: ${trafficImprovement >= 0 ? '+' : ''}${trafficImprovement}
- Users: ${traffic.users || 0}
- Avg session duration: ${traffic.avgDuration || 'not provided'}
${commentary.trafficCommentary ? 'Traffic commentary: ' + commentary.trafficCommentary : ''}

CONVERSIONS:
- Phone calls: ${conversions.calls || 0} (Day 1: ${baseline.calls || 0})
- Email enquiries: ${conversions.emails || 0} (Day 1: ${baseline.emails || 0})
- Form submissions: ${conversions.forms || 0} (Day 1: ${baseline.forms || 0})
${conversions.other ? '- Other: ' + conversions.other + ' ' + (conversions.otherLabel || '') : ''}
- Total conversions: ${totalConversions} (Day 1: ${baselineConversions})

ACTIONS COMPLETED:
Technical SEO: ${actions.technical.filter(a => a.trim()).join(', ') || 'None listed'}
Content: ${actions.content.filter(a => a.trim()).join(', ') || 'None listed'}
Backlinks: ${actions.backlinks.filter(a => a.trim()).join(', ') || 'None listed'}
Citations: ${actions.citations.filter(a => a.trim()).join(', ') || 'None listed'}

${commentary.plannedContent ? 'NEXT MONTH PLANNED: ' + commentary.plannedContent : ''}
${commentary.newIdeas ? 'NEW IDEAS: ' + commentary.newIdeas : ''}
${commentary.summary ? 'ADDITIONAL NOTES: ' + commentary.summary : ''}

Write a professional, warm and positive executive summary of 180-250 words. Structure it as flowing paragraphs (not bullet points). Include:
1. Opening — acknowledge the month's overall performance positively
2. Keyword wins — highlight the most impressive improvements
3. Traffic and conversion performance — be specific with numbers
4. What was done this month — briefly summarise the work completed
5. Looking ahead — mention next steps and any new ideas
6. Closing — a reassuring, forward-looking sentence

Tone: professional but warm, confident, client-friendly. Avoid jargon. Write in third person referring to "the team" or "our work". Use UK English.`;

      const res = await fetch(`${API_URL}/api/content/meta-batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt, maxTokens: 600 })
      });
      const result = await res.json();
      setSummary(result.result || '');
    } catch (e) {
      setError('Could not generate executive summary — you can write it manually in the preview.');
      console.error(e);
    }

    setStage('preview');
  };

  const handleSave = async () => {
    if (!reportData) return;
    setSaving(true);
    try {
      await supabase.from('reports').insert([{
        client_id: reportData.clientId || null,
        report_name: reportData.reportName,
        report_month: new Date().toISOString().slice(0, 7),
        executive_summary: summary,
        data: {
          keywords: reportData.keywords,
          traffic: reportData.traffic,
          conversions: reportData.conversions,
          actions: reportData.actions,
          commentary: reportData.commentary,
          baseline: reportData.baseline,
        }
      }]);
    } catch (e) {
      console.error('Save error:', e);
    }
    setSaving(false);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1.5rem' }}>
        <button className="btn btn-sm" onClick={onBack}>← Tools</button>
        <div>
          <div className="page-title">Monthly SEO Report</div>
          <div className="page-sub">Generate a professional client report with charts, actions and AI executive summary</div>
        </div>
      </div>

      {error && <div className="alert alert-warning" style={{ marginBottom: 12 }}>{error}</div>}

      {stage === 'form' && (
        <ReportForm clients={clients} onGenerate={handleGenerate} />
      )}

      {stage === 'generating' && (
        <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
          <span className="spinner" style={{ width: 28, height: 28 }} />
          <div style={{ fontSize: 14, fontWeight: 500, marginTop: 16, marginBottom: 6 }}>Generating your report…</div>
          <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>Writing executive summary with AI</div>
        </div>
      )}

      {stage === 'preview' && reportData && (
        <ReportPreview
          data={reportData}
          summary={summary}
          onBack={() => setStage('form')}
          onSave={handleSave}
          saving={saving}
        />
      )}
    </div>
  );
}
